/**
 * EventForm — form for creating or editing a calendar event.
 *
 * Fields: title (1-200 chars), start date/time, end date/time,
 * description (0-2000 chars), location, isAllDay toggle, color picker.
 * Validates that start < end before submission.
 *
 * Uses useCalendarStore.createEvent/updateEvent for persistence.
 *
 * @see Requirements 5.6, 6.1, 6.6, 6.7, 17.5
 */

import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { useCalendarStore } from '../../stores';
import type { CalendarEvent } from '../../types/calendar';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';

export interface EventFormProps {
  /** Existing event to edit, or undefined for creation mode. */
  event?: CalendarEvent;
  /** Pre-filled start date in YYYY-MM-DD format (used when creating from day click). */
  defaultDate?: string;
  /** Called after a successful create or update. */
  onSuccess?: () => void;
  /** Called when user cancels the form. */
  onCancel?: () => void;
}

/** Predefined color options matching the design system palette. */
const COLOR_OPTIONS = [
  { value: '#7C6EAF', label: 'Lavender' },
  { value: '#F5A5B8', label: 'Rose' },
  { value: '#B8D4E3', label: 'Sky Blue' },
  { value: '#F2D4A7', label: 'Warm Gold' },
  { value: '#A8E6CF', label: 'Mint' },
  { value: '#FFD3E0', label: 'Light Pink' },
  { value: '#C3B1E1', label: 'Soft Purple' },
];

/** Maximum title length. */
const MAX_TITLE_LENGTH = 200;

/** Maximum description length. */
const MAX_DESCRIPTION_LENGTH = 2000;

/**
 * Extract date (YYYY-MM-DD) from an ISO datetime string.
 */
function extractDate(isoStr: string): string {
  return isoStr.split('T')[0] ?? isoStr;
}

/**
 * Extract time (HH:mm) from an ISO datetime string.
 */
function extractTime(isoStr: string): string {
  const timePart = isoStr.split('T')[1];
  if (!timePart) return '09:00';
  return timePart.substring(0, 5);
}

/**
 * Combine a date (YYYY-MM-DD) and time (HH:mm) into an ISO 8601 datetime string.
 */
function combineDateTime(date: string, time: string): string {
  return `${date}T${time}:00`;
}

export function EventForm({ event, defaultDate, onSuccess, onCancel }: EventFormProps) {
  const isEditing = !!event;

  // Initialize form state from existing event or defaults
  const [title, setTitle] = useState(event?.title ?? '');
  const [startDate, setStartDate] = useState(
    event ? extractDate(event.startDate) : defaultDate ?? ''
  );
  const [startTime, setStartTime] = useState(
    event ? extractTime(event.startDate) : '09:00'
  );
  const [endDate, setEndDate] = useState(
    event ? extractDate(event.endDate) : defaultDate ?? ''
  );
  const [endTime, setEndTime] = useState(
    event ? extractTime(event.endDate) : '10:00'
  );
  const [description, setDescription] = useState(event?.description ?? '');
  const [location, setLocation] = useState(event?.location ?? '');
  const [isAllDay, setIsAllDay] = useState(event?.isAllDay ?? false);
  const [color, setColor] = useState<string>(
    event?.color ?? COLOR_OPTIONS[0]?.value ?? '#7C6EAF'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const createEvent = useCalendarStore((s) => s.createEvent);
  const updateEvent = useCalendarStore((s) => s.updateEvent);

  // When isAllDay changes, adjust times
  useEffect(() => {
    if (isAllDay) {
      setStartTime('00:00');
      setEndTime('23:59');
    }
  }, [isAllDay]);

  /**
   * Validate form inputs.
   * Returns error message or null if valid.
   */
  const validateForm = useCallback((): string | null => {
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) {
      return 'Title is required.';
    }
    if (trimmedTitle.length > MAX_TITLE_LENGTH) {
      return `Title must not exceed ${MAX_TITLE_LENGTH} characters.`;
    }

    if (!startDate) {
      return 'Start date is required.';
    }
    if (!endDate) {
      return 'End date is required.';
    }

    if (description.length > MAX_DESCRIPTION_LENGTH) {
      return `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
    }

    // Validate start < end
    const startDateTime = combineDateTime(startDate, startTime);
    const endDateTime = combineDateTime(endDate, endTime);
    if (startDateTime >= endDateTime) {
      return 'End date/time must be after start date/time.';
    }

    return null;
  }, [title, startDate, endDate, startTime, endTime, description]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setFormError(null);

      const validationError = validateForm();
      if (validationError) {
        setFormError(validationError);
        return;
      }

      setIsSubmitting(true);

      try {
        const startDateTime = isAllDay
          ? `${startDate}T00:00:00`
          : combineDateTime(startDate, startTime);
        const endDateTime = isAllDay
          ? `${endDate}T23:59:59`
          : combineDateTime(endDate, endTime);

        if (isEditing && event) {
          await updateEvent(event.eventId, {
            title: title.trim(),
            startDate: startDateTime,
            endDate: endDateTime,
            description: description.trim(),
            location: location.trim() || null,
            isAllDay,
            color,
          });
        } else {
          await createEvent({
            title: title.trim(),
            startDate: startDateTime,
            endDate: endDateTime,
            description: description.trim(),
            location: location.trim() || undefined,
            isAllDay,
            color,
          });
        }

        onSuccess?.();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to save event.';
        setFormError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      title, startDate, startTime, endDate, endTime, description,
      location, isAllDay, color, isEditing, event,
      createEvent, updateEvent, onSuccess, validateForm,
    ]
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 p-4"
      aria-label={isEditing ? 'Edit event form' : 'Create event form'}
      noValidate
    >
      <h2 className="text-lg font-semibold text-text-heading">
        {isEditing ? 'Edit Event' : 'New Event'}
      </h2>

      {/* Error message */}
      {formError && (
        <div
          role="alert"
          className="rounded-sm border border-primary-dark/30 bg-primary-dark/5 px-3 py-2 text-sm text-primary-dark"
        >
          {formError}
        </div>
      )}

      {/* Title */}
      <Input
        label="Title"
        placeholder="e.g. Team meeting, Doctor appointment"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={MAX_TITLE_LENGTH}
        required
        disabled={isSubmitting}
        aria-describedby="title-hint"
      />
      <span id="title-hint" className="sr-only">
        1 to 200 characters
      </span>

      {/* All Day toggle */}
      <div className="flex items-center gap-3">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isAllDay}
            onChange={(e) => setIsAllDay(e.target.checked)}
            disabled={isSubmitting}
            className="sr-only peer"
            aria-label="All day event"
          />
          <div
            className={[
              'w-9 h-5 rounded-full transition-colors duration-200',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-focus-ring peer-focus-visible:ring-offset-2',
              'peer-disabled:opacity-50',
              isAllDay ? 'bg-primary' : 'bg-border',
            ].join(' ')}
          >
            <div
              className={[
                'w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 mt-0.5',
                isAllDay ? 'translate-x-4.5 ml-0.5' : 'translate-x-0.5',
              ].join(' ')}
            />
          </div>
        </label>
        <span className="text-sm font-medium text-text-secondary">All day</span>
      </div>

      {/* Start Date / Time */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
          disabled={isSubmitting}
        />
        {!isAllDay && (
          <Input
            label="Start Time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
            disabled={isSubmitting}
          />
        )}
      </div>

      {/* End Date / Time */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
          disabled={isSubmitting}
        />
        {!isAllDay && (
          <Input
            label="End Time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
            disabled={isSubmitting}
          />
        )}
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="event-description"
          className="text-sm font-medium text-text-secondary"
        >
          Description (optional)
        </label>
        <textarea
          id="event-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={MAX_DESCRIPTION_LENGTH}
          rows={3}
          disabled={isSubmitting}
          placeholder="Add details about this event..."
          className={[
            'w-full rounded-sm border bg-bg-card text-text-primary placeholder:text-text-secondary/60',
            'transition-all duration-fast ease-fast resize-y',
            'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary',
            'border-border hover:border-secondary-light',
            'disabled:opacity-50 disabled:pointer-events-none',
            'px-4 py-2 text-body',
          ].join(' ')}
          aria-describedby="description-hint"
        />
        <div className="flex items-center justify-between">
          <span id="description-hint" className="sr-only">
            Up to 2000 characters
          </span>
          <span className="text-xs text-text-secondary ml-auto">
            {description.length}/{MAX_DESCRIPTION_LENGTH}
          </span>
        </div>
      </div>

      {/* Location */}
      <Input
        label="Location (optional)"
        placeholder="e.g. Office, Conference Room B"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        disabled={isSubmitting}
      />

      {/* Color Picker */}
      <fieldset className="flex flex-col gap-1.5" disabled={isSubmitting}>
        <legend className="text-sm font-medium text-text-secondary">Color</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Select event color">
          {COLOR_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={color === option.value}
              aria-label={option.label}
              onClick={() => setColor(option.value)}
              className={[
                'w-7 h-7 rounded-full border-2 transition-all duration-fast ease-fast',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
                color === option.value
                  ? 'border-text-heading scale-110'
                  : 'border-transparent hover:scale-105',
              ].join(' ')}
              style={{ backgroundColor: option.value }}
            />
          ))}
        </div>
      </fieldset>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? isEditing ? 'Saving...' : 'Creating...'
            : isEditing ? 'Save Changes' : 'Create Event'}
        </Button>
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

EventForm.displayName = 'EventForm';
